import { useState, useMemo } from 'react';
import { useAmplifyData } from '../hooks/useAmplifyData';
import { localReportService } from '../services/localDataService';
import type { Report as ReportType } from '../models/index.js';
import { Report as ReportModel } from '../models/index.js';
import './Screen.css';

// Use local service in local mode
const reportService = localReportService;

export default function Reports() {
  const [selectedType, setSelectedType] = useState<string>('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const { data: reports, loading, refetch } = useAmplifyData<ReportType>(ReportModel);

  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchesType = selectedType === 'all' || report.type === selectedType;
      const matchesDate = (!dateRange.start || (report.generatedAt && new Date(report.generatedAt) >= new Date(dateRange.start))) &&
        (!dateRange.end || (report.generatedAt && new Date(report.generatedAt) <= new Date(dateRange.end)));
      return matchesType && matchesDate;
    });
  }, [reports, selectedType, dateRange]);

  const handleGenerateReport = async (type: string) => {
    try {
      await reportService.create({
        title: `${type} Report`,
        type,
        generatedAt: new Date().toISOString(),
        data: '{}',
        status: 'pending',
      });
      refetch();
    } catch (error) {
      console.error('Failed to generate report:', error);
    }
  };

  const reportTypes = ['summary', 'detailed', 'analytics', 'export'];

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Reports</h1>
        <p className="screen-subtitle">Generate and view reports</p>
      </div>

      <div className="toolbar">
        <div className="filters">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            {reportTypes.map(type => (
              <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            className="date-input"
            placeholder="Start Date"
          />
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            className="date-input"
            placeholder="End Date"
          />
        </div>
        <div className="action-buttons">
          {reportTypes.map(type => (
            <button
              key={type}
              className="btn btn-secondary"
              onClick={() => handleGenerateReport(type)}
            >
              Generate {type}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading reports...</div>
      ) : (
        <div className="reports-list">
          {filteredReports.length > 0 ? (
            filteredReports.map((report) => (
              <div key={report.id} className="report-card">
                <div className="card-header">
                  <h3>{report.title}</h3>
                  <span className={`status-badge status-${report.status}`}>
                    {report.status}
                  </span>
                </div>
                <div className="card-body">
                  <p><strong>Type:</strong> {report.type}</p>
                  <p><strong>Generated:</strong> {report.generatedAt ? new Date(report.generatedAt).toLocaleString() : 'N/A'}</p>
                </div>
                <div className="card-footer">
                  <button className="btn btn-primary">View</button>
                  <button className="btn btn-secondary">Download</button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <p>No reports found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

